/**
 * Partition service-request quotations into vendor (Rajdoot) vs branch (Rajput) buckets.
 */
export function isVendorQuotation(quotation, vendorId) {
  if (!quotation) return false;
  if (vendorId && quotation.requestedBy != null) {
    return String(quotation.requestedBy) === String(vendorId);
  }
  if (quotation.isLaborCost === true) return true;
  const type = (quotation.type || "").toLowerCase();
  return type.includes("labor");
}

export function partitionQuotations(quotations, vendorId) {
  const list = Array.isArray(quotations) ? quotations : [];
  const vendor = [];
  const rajput = [];
  list.forEach(q => {
    if (isVendorQuotation(q, vendorId)) {
      vendor.push(q);
    } else {
      rajput.push(q);
    }
  });
  return { vendor, rajput };
}

export function quotationStatusCounts(quotations) {
  const list = Array.isArray(quotations) ? quotations : [];
  let accepted = 0;
  let pending = 0;
  let rejected = 0;

  list.forEach(q => {
    const code = q.status && q.status.code;
    if (code === "accepted") accepted += 1;
    else if (code === "declined") rejected += 1;
    else if (code === "pending" || code === "dispatched") pending += 1;
  });

  return {
    total: list.length,
    accepted,
    pending,
    rejected
  };
}

export function vendorDisplayName(serviceRequest) {
  if (!serviceRequest || !serviceRequest.vendor) return "Vendor";
  const v = serviceRequest.vendor;
  return [v.firstName, v.lastName].filter(Boolean).join(" ") || "Vendor";
}

/** Role label from login user / localStorage user payload. */
export function getLoggedInUserRoleLabel(user) {
  if (!user || typeof user !== "object") return "";
  if (user.role && typeof user.role === "object") {
    return (
      user.role.label ||
      user.role.name ||
      user.role.code ||
      ""
    ).toString();
  }
  if (typeof user.role === "string") return user.role;
  return (user.roleLabel || user.role_name || "").toString();
}

const FLOW_EDIT_ROLE_HINTS = [
  "admin",
  "super admin",
  "superadmin",
  "manager",
  "owner",
  "accountant"
];

const FLOW_VIEW_ONLY_ROLE_HINTS = ["vendor", "staff", "employee", "technician"];

function roleStringIncludesAny(roleText, hints) {
  const normalized = (roleText || "").trim().toLowerCase();
  if (!normalized) return false;
  return hints.some(hint => normalized === hint || normalized.includes(hint));
}

function userHasViewOnlyRoleSignals(user) {
  if (user.isStaff === true || user.is_staff === true) return true;
  if (user.isVendor === true || user.is_vendor === true) return true;

  const typeFields = [user.userType, user.user_type, user.type, user.accountType]
    .filter(Boolean)
    .map(v => String(v).toLowerCase());
  if (typeFields.some(t => roleStringIncludesAny(t, FLOW_VIEW_ONLY_ROLE_HINTS))) {
    return true;
  }

  const roles = user.roles;
  if (Array.isArray(roles)) {
    return roles.some(r => {
      const text =
        typeof r === "string"
          ? r
          : (r && (r.label || r.name || r.code)) || "";
      return roleStringIncludesAny(text, FLOW_VIEW_ONLY_ROLE_HINTS);
    });
  }

  return false;
}

/** Service Request Flow is view-only for vendor and staff accounts. */
export function isServiceRequestFlowReadOnly(user) {
  if (!user || typeof user !== "object") return false;

  const label = getLoggedInUserRoleLabel(user).trim().toLowerCase();

  if (label) {
    const isEditor = FLOW_EDIT_ROLE_HINTS.some(
      hint => label === hint || label.includes(hint)
    );
    if (isEditor) return false;
    if (roleStringIncludesAny(label, FLOW_VIEW_ONLY_ROLE_HINTS)) return true;
  }

  if (userHasViewOnlyRoleSignals(user)) return true;

  // Vendor portal logins often carry vendorId without a normalized role label.
  if (user.vendorId != null || user.vendor_id != null) return true;

  return false;
}

export function resolveLoggedInUser(store) {
  if (!store) return null;
  const fromStore = store.getters && store.getters["user/getLoggedInUser"];
  if (fromStore && Object.keys(fromStore).length) return fromStore;
  try {
    const raw = window.localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function pickNumericField(source, keys) {
  if (!source || typeof source !== "object") return null;
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (source[key] == null || source[key] === "") continue;
    const value = parseFloat(source[key]);
    if (!Number.isNaN(value)) return value;
  }
  return null;
}

function sumQuotationListTotals(quotations) {
  const list = Array.isArray(quotations) ? quotations : [];
  return list.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);
}

function computeRequestAgeDays(serviceRequest) {
  const sr = serviceRequest || {};
  const fromApi = pickNumericField(sr, [
    "remainingDays",
    "remaining_days",
    "requestAge",
    "request_age",
    "ageInDays",
    "age_in_days",
    "requestDays",
    "request_days"
  ]);
  if (fromApi != null) return Math.max(0, Math.round(fromApi));

  const dateRaw =
    sr.date || sr.createdAt || sr.created_at || sr.requestDate || sr.request_date;
  if (!dateRaw) return 0;
  const start = new Date(dateRaw);
  if (Number.isNaN(start.getTime())) return 0;
  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - start.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

function computeExpenseSoFar(serviceRequest, accountDetails) {
  const sr = serviceRequest || {};
  const ad = accountDetails || {};

  const fromServiceRequest = pickNumericField(sr, [
    "expenseSoFar",
    "expense_so_far",
    "expenseTillDate",
    "expense_till_date",
    "totalExpense",
    "total_expense",
    "expenseAmount",
    "expense_amount"
  ]);
  if (fromServiceRequest != null) return fromServiceRequest;

  const fromAccount = pickNumericField(ad, [
    "expenseSoFar",
    "expense_so_far",
    "expenseTillDate",
    "expense_till_date",
    "totalExpense",
    "total_expense",
    "expenseAmount",
    "expense_amount"
  ]);
  if (fromAccount != null) return fromAccount;

  let total = 0;
  total += parseFloat(ad.chalanTotal) || 0;
  total += parseFloat(ad.externalGoodsTotal) || 0;
  total += parseFloat(ad.internalGoodsTotal) || 0;
  total += parseFloat(ad.serviceRequestLaborTotal) || 0;
  if (total > 0) return total;

  total += parseFloat(sr.externalGoodsTotal) || 0;
  return total >= 0 ? total : 0;
}

/** Header summary metrics for Service Request Flow (quotation, expense, age in days). */
export function resolveServiceRequestFlowSummary({
  serviceRequest,
  quotations,
  accountDetails
}) {
  const sr = serviceRequest || {};

  let quotationAmount = pickNumericField(sr, [
    "quotationAmount",
    "quotation_amount",
    "totalQuotation",
    "total_quotation",
    "quotationTotal",
    "quotation_total"
  ]);
  if (quotationAmount == null) {
    quotationAmount = sumQuotationListTotals(quotations);
  }

  return {
    quotationAmount: quotationAmount != null ? quotationAmount : 0,
    expenseSoFar: computeExpenseSoFar(sr, accountDetails),
    remainingDays: computeRequestAgeDays(sr)
  };
}

export function formatServiceRequestSummaryAmount(amount) {
  const value = parseFloat(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(value);
}
